import React, { useState } from 'react';
import { apiFetch, useToast } from '../App';
import { ga } from '../utils/ga4';
import { sharePage } from '../utils/share';
import ParallaxBackground from '../components/ParallaxBackground';
import { Building2, Truck, Box, ChevronDown, Check, ArrowRight, DollarSign, Users, Zap, Award, BookOpen, Phone, Mail, MapPin, TrendingUp, Loader2, Share2 } from 'lucide-react';

const MODELS = [
  {
    icon: Building2, title: 'Experience Store', format: 'STORE',
    investment: '₹20-25 Lakhs', revenue: '₹6-8 Lakhs/mo', profit: '₹3.5-5 Lakhs/mo', roi: '14-18 months',
    color: '#FF8C42', desc: 'Full-service grooming studio in high-traffic retail locations. Premium ambiance, multiple grooming stations, and retail area.',
    details: ['400-600 sq ft space', '3-4 grooming stations', 'Retail product display', 'Waiting lounge for pet parents', 'Live streaming capability', 'Walk-in and appointment based'],
  },
  {
    icon: Box, title: 'Grooming Pod', format: 'POD',
    investment: '₹12-15 Lakhs', revenue: '₹2.5-3.5 Lakhs/mo', profit: '₹1-2 Lakhs/mo', roi: '12-15 months',
    color: '#8B5CF6', desc: 'Compact grooming pods in malls, apartment complexes, and commercial spaces. Lower investment, high convenience.',
    details: ['150-250 sq ft space', '1-2 grooming stations', 'Mall or residential placement', 'Self-service options', 'Low overhead costs', 'Quick setup (2-3 weeks)'],
  },
  {
    icon: Truck, title: 'Mobile Van', format: 'VAN',
    investment: '₹15-18 Lakhs', revenue: '₹1.2-1.8 Lakhs/mo', profit: '₹0.5-1 Lakh/mo', roi: '14-16 months',
    color: '#20B2AA', desc: 'Fully equipped mobile grooming van serving doorstep appointments across a city zone. Maximum convenience.',
    details: ['Custom-fitted van', 'Complete grooming setup', 'Water & power self-sufficient', 'GPS-tracked routes', 'Doorstep service', 'Covers 15-20 clients/day'],
  },
];

const VALUE_PROPS = [
  { icon: TrendingUp, title: 'Proven Business Model', desc: 'ROI in 12-18 months with our tested operational playbook' },
  { icon: Award, title: 'Celebrity Brand', desc: 'Strong brand recognition and marketing support from day one' },
  { icon: BookOpen, title: 'Complete Training', desc: '3-week intensive training for you and your team at Petfolk HQ' },
  { icon: Zap, title: 'World-Class Tech', desc: 'Our booking platform, CRM, and analytics built for your success' },
  { icon: Users, title: 'Marketing Support', desc: 'Local marketing campaigns, social media, and customer acquisition' },
  { icon: DollarSign, title: 'Exclusive Territory', desc: 'Protected territory rights ensure no competing Petfolk nearby' },
];

// Hyderabad territories — `taken: true` means a Petfolk is now live there (rendered with strikethrough, non-clickable)
const HYDERABAD_TERRITORIES = [
  { name: 'JNTU Metro Station',         taken: true,  takenAs: 'KPHB',          slug: '/kphb' },
  { name: 'Victoria Memorial Metro Station', taken: true, takenAs: 'LB Nagar',  slug: '/lb-nagar' },
  { name: 'Banjara Hills',              taken: true,  takenAs: 'Banjara Hills', slug: '/banjara-hills' },
  { name: 'Kondapur',                   taken: true,  takenAs: 'Kondapur',      slug: '/kondapur' },
  { name: 'Sainikpuri',                 taken: false },
  { name: 'Nallagandla',                taken: false },
  { name: 'Uppal',                      taken: false },
  { name: 'Jubilee Hills',              taken: false },
  { name: 'Kokapet',                    taken: false },
  { name: 'Madhapur',                   taken: false },
];

const UPCOMING_HYDERABAD = HYDERABAD_TERRITORIES.filter(t => !t.taken).map(t => t.name);

const UPCOMING_CITIES = ['Vizag', 'Vijayawada', 'Bengaluru', 'Pune', 'Chennai', 'Kolkata', 'Delhi'];

// Build flat list for the form dropdown
const LOCATION_OPTIONS = [
  { group: 'Upcoming — Hyderabad', items: UPCOMING_HYDERABAD.map(l => ({ value: `Hyderabad - ${l}`, label: `${l}, Hyderabad` })) },
  { group: 'Upcoming — Cities', items: UPCOMING_CITIES.map(c => ({ value: c, label: c })) },
  { group: 'Other', items: [{ value: 'Other', label: 'Other (specify in message)' }] },
];

export default function FranchisePage() {
  const addToast = useToast();
  const [expandedModel, setExpandedModel] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', locationInterested: '', formatInterested: 'STORE', budgetRange: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.locationInterested) {
      addToast('Please fill in required fields', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch('/api/franchise/leads', { method: 'POST', body: JSON.stringify({
        name: form.name,
        phone: form.phone,
        email: form.email,
        city: form.locationInterested,
        franchiseModel: form.formatInterested,
        investmentBudget: form.budgetRange,
        message: form.message,
      }) });
      ga.franchiseLead(form.locationInterested, form.formatInterested, form.budgetRange);
      setSubmitted(true);
      addToast('Application submitted! We\'ll contact you soon.');
    } catch (e) { addToast(e.message, 'error'); }
    setSubmitting(false);
  };

  return (
    <div className="relative min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <ParallaxBackground />

      {/* Hero */}
      <section data-testid="franchise-hero" className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-6" style={{ background: 'rgba(255,140,66,0.1)', color: 'var(--petfolk-orange)' }}>
            Franchise Opportunity
          </span>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6" style={{ color: 'var(--text-primary)' }}>
            Build Your Pet Care{' '}
            <span className="gradient-text">Empire</span>
          </h1>
          <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Join India's fastest-growing pet grooming franchise. Proven model, premium brand, complete support.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#apply" className="btn-primary text-lg px-8 py-4" data-testid="franchise-apply-cta">
              Request Franchise Info <ArrowRight size={20} />
            </a>
            <a href="#models" className="btn-outline text-lg px-8 py-3.5">
              Explore Models
            </a>
            <button
              onClick={async () => {
                const m = await sharePage({
                  title: 'Own a Petfolk Franchise',
                  text: 'Partner with India\'s fastest-growing pet grooming franchise.',
                  type: 'franchise',
                });
                if (m === 'clipboard') addToast('Link copied to clipboard');
              }}
              className="btn-outline text-lg px-6 py-3.5 inline-flex items-center gap-2"
              data-testid="franchise-share-btn"
            >
              <Share2 size={18} /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Available Territories — Position #2 */}
      <section className="relative py-16 z-10" data-testid="territories-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-outfit text-3xl font-bold text-center mb-4" style={{ color: 'var(--text-primary)' }}>
            Available <span className="gradient-text">Territories</span>
          </h2>
          <p className="text-center mb-10" style={{ color: 'var(--text-secondary)' }}>We're expanding fast! Grab your exclusive territory before it's taken.</p>

          {/* Hyderabad territories — taken stores show as strikethrough */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-petfolk-orange" />
              <h3 className="font-outfit text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Hyderabad — Territories</h3>
              <span className="text-xs ml-2 px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(34,197,94,0.1)', color: '#15803d' }}>
                {HYDERABAD_TERRITORIES.filter(t => t.taken).length} taken
              </span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {HYDERABAD_TERRITORIES.map(loc => (
                loc.taken ? (
                  <a
                    key={loc.name}
                    href={loc.slug || '#'}
                    title={`Petfolk is now LIVE here as ${loc.takenAs}`}
                    className="px-4 py-2 rounded-2xl text-sm font-semibold petfolk-card transition-all cursor-not-allowed flex items-center gap-2"
                    style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
                    data-testid={`territory-taken-${loc.name.replace(/\s/g, '-')}`}
                  >
                    <span style={{ textDecoration: 'line-through' }}>{loc.name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-md font-medium" style={{ background: 'rgba(34,197,94,0.12)', color: '#15803d' }}>
                      Taken
                    </span>
                  </a>
                ) : (
                  <a
                    key={loc.name}
                    href="#apply"
                    onClick={() => setForm(f => ({ ...f, locationInterested: `Hyderabad - ${loc.name}` }))}
                    className="px-4 py-2 rounded-2xl text-sm font-semibold petfolk-card hover:ring-2 hover:ring-petfolk-orange/40 transition-all cursor-pointer"
                    style={{ color: 'var(--text-primary)' }}
                    data-testid={`territory-${loc.name.replace(/\s/g, '-')}`}
                  >
                    {loc.name}
                  </a>
                )
              ))}
            </div>
          </div>

          {/* Upcoming cities */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={18} className="text-petfolk-purple" />
              <h3 className="font-outfit text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Upcoming Cities</h3>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {UPCOMING_CITIES.map(city => (
                <a key={city} href="#apply" onClick={() => setForm(f => ({ ...f, locationInterested: city }))}
                  className="px-5 py-2.5 rounded-2xl text-sm font-semibold petfolk-card hover:ring-2 hover:ring-petfolk-purple/40 transition-all cursor-pointer"
                  style={{ color: 'var(--text-primary)' }} data-testid={`territory-city-${city}`}>
                  {city}
                </a>
              ))}
              <a href="#apply" className="px-5 py-2.5 rounded-2xl text-sm font-semibold italic cursor-pointer hover:ring-2 hover:ring-petfolk-orange/40 transition-all"
                style={{ color: 'var(--text-secondary)', border: '1px dashed var(--border-color)' }}
                onClick={() => setForm(f => ({ ...f, locationInterested: 'Other' }))}>
                + Your city?
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="relative py-16 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-outfit text-3xl font-bold text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            Why <span className="gradient-text">Petfolk</span> Franchise?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUE_PROPS.map((vp, i) => (
              <div key={i} className="petfolk-card p-6 flex gap-4" data-testid={`franchise-vp-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-petfolk-orange/15 to-petfolk-purple/10 flex items-center justify-center shrink-0">
                  <vp.icon size={22} className="text-petfolk-orange" />
                </div>
                <div>
                  <h3 className="font-outfit font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{vp.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{vp.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Franchise Models */}
      <section id="models" className="relative py-16 z-10" data-testid="franchise-models">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-outfit text-3xl font-bold text-center mb-4" style={{ color: 'var(--text-primary)' }}>
            Choose Your <span className="gradient-text">Format</span>
          </h2>
          <p className="text-center mb-12" style={{ color: 'var(--text-secondary)' }}>Three proven models to match your investment and goals</p>

          <div className="grid lg:grid-cols-3 gap-8">
            {MODELS.map((m, i) => (
              <div key={i} className="petfolk-card overflow-hidden" data-testid={`franchise-model-${m.format}`}>
                <div className="p-1.5" style={{ background: m.color }}>
                  <div className="p-6" style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0' }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${m.color}15` }}>
                        <m.icon size={24} style={{ color: m.color }} />
                      </div>
                      <h3 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{m.title}</h3>
                    </div>
                    <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{m.desc}</p>

                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Investment</span><span className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{m.investment}</span></div>
                      <div className="flex justify-between"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Monthly Revenue</span><span className="font-outfit font-bold text-green-600">{m.revenue}</span></div>
                      <div className="flex justify-between"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Expected Profit</span><span className="font-outfit font-bold" style={{ color: m.color }}>{m.profit}</span></div>
                      <div className="flex justify-between"><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>ROI Period</span><span className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{m.roi}</span></div>
                    </div>

                    <button
                      onClick={() => setExpandedModel(expandedModel === i ? null : i)}
                      className="text-sm font-semibold flex items-center gap-1 mb-4"
                      style={{ color: m.color }}
                    >
                      {expandedModel === i ? 'Hide' : 'View'} Details
                      <ChevronDown size={14} className={`transition-transform ${expandedModel === i ? 'rotate-180' : ''}`} />
                    </button>

                    {expandedModel === i && (
                      <div className="space-y-2 animate-fade-in mb-4">
                        {m.details.map((d, j) => (
                          <div key={j} className="flex items-center gap-2 text-sm">
                            <Check size={14} style={{ color: m.color }} />
                            <span style={{ color: 'var(--text-secondary)' }}>{d}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <a href="#apply" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white text-sm" style={{ background: m.color }} onClick={() => setForm({...form, formatInterested: m.format})}>
                      Apply for {m.title} <ArrowRight size={16} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="relative py-16 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-outfit text-3xl font-bold text-center mb-8" style={{ color: 'var(--text-primary)' }}>
            What's <span className="gradient-text">Included</span>
          </h2>
          <div className="petfolk-card p-8">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Complete store/pod/van setup assistance',
                'Petfolk branding & signage kit',
                '3-week intensive groomer training',
                'Booking & operations software access',
                'Marketing launch campaign',
                'Product supply chain access',
                'Ongoing operational support',
                'Quarterly business reviews',
                'Customer acquisition support',
                'Quality assurance audits',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-green-600" />
                  </div>
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="relative py-16 z-10" data-testid="franchise-form">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="petfolk-card p-8 sm:p-10">
            <h2 className="font-outfit text-2xl font-bold text-center mb-2" style={{ color: 'var(--text-primary)' }}>
              {submitted ? 'Application Received!' : 'Apply for Franchise'}
            </h2>

            {submitted ? (
              <div className="text-center py-8 animate-fade-in">
                <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <Check size={36} className="text-green-600" />
                </div>
                <p className="text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Thank you for your interest!</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Our franchise team will reach out within 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Full Name *</label>
                    <input className="petfolk-input" placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="franchise-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Phone *</label>
                    <input className="petfolk-input" placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="franchise-phone" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Email</label>
                  <input className="petfolk-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="franchise-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Location Interested *</label>
                    <select className="petfolk-input" value={form.locationInterested} onChange={e => setForm({...form, locationInterested: e.target.value})} required data-testid="franchise-location">
                      <option value="">Select location</option>
                      {LOCATION_OPTIONS.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.items.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Format Preferred</label>
                    <select className="petfolk-input" value={form.formatInterested} onChange={e => setForm({...form, formatInterested: e.target.value})} data-testid="franchise-format">
                      <option value="STORE">Experience Store</option>
                      <option value="POD">Grooming Pod</option>
                      <option value="VAN">Mobile Van</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Investment Budget</label>
                  <select className="petfolk-input" value={form.budgetRange} onChange={e => setForm({...form, budgetRange: e.target.value})} data-testid="franchise-budget">
                    <option value="">Select range</option>
                    <option value="10-15L">₹10-15 Lakhs</option>
                    <option value="15-20L">₹15-20 Lakhs</option>
                    <option value="20-25L">₹20-25 Lakhs</option>
                    <option value="25L+">₹25 Lakhs+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Message / Questions</label>
                  <textarea className="petfolk-input" rows={3} placeholder="Tell us about yourself and any questions..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} data-testid="franchise-message" />
                </div>
                <button type="submit" className="btn-primary w-full justify-center text-lg py-4" disabled={submitting} data-testid="franchise-submit-btn">
                  {submitting ? <Loader2 className="animate-spin" size={20} /> : <>Submit Application <ArrowRight size={18} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
